/**
 * modal.service.ts
 * Copyright © 2016-2019, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { ContainerContent, DialogRef, ModalComponent, overlayConfigFactory }
  from "angular2-modal";
import { BSModalContext, Modal, TwoButtonPreset, TwoButtonPresetBuilder }
  from "angular2-modal/plugins/bootstrap";
import { Observable, Observer } from "rxjs/Rx";

interface MessageContext {
  title : string;
  body : string;
}

export interface Inspectable
{
  inspect : () => MessageContext;
}

export interface ConfirmationContext extends MessageContext {
  yesLabel? : string;
  noLabel? : string;
  cancelLabel? : string;
  dismissValue? : any;
}

interface PromptContext<T> extends MessageContext {
  placeholder : T;
  acceptEmptyResult? : boolean;
  initialValue? : string;
}

/**
 * ModalService provides a wrapper for the angular2-modal library to simplify
 * our interactions a bit, as well as introduce a few extra standard modal
 * options.
 */
@Injectable()
export class ModalService
{
  constructor (private modalProvider : Modal)
  {
    /* DI */
  }

  private open<T> (
    componentClass : ContainerContent,
    configuration : {[key : string] : any} = {}
  ) : Promise<DialogRef<T>>
  {
    return this.modalProvider.open(
      componentClass,
      overlayConfigFactory(configuration, BSModalContext)
    );
  }

  /**
   * Open a particular ModalComponent into a modal window, with no return
   * behavior.
   *
   * @param componentClass ModalComponent to be opened
   * @param configuration The context & config values for the window
   */
  public display (
    componentClass : ContainerContent,
    configuration : {[key : string] : any} = {}
  ) : Observable<() => void>
  {
    const promise : Promise<() => void> =
      this.open(componentClass, configuration).then((dr) => dr.close.bind(dr));
    return Observable.fromPromise(promise);
  }

  // public ask<T> (
  //   componentClass : ContainerContent,
  //   configuration : {[key : string] : any} = {},
  //   followup : (result : T) => void,
  //   failure : (err : any) => void = (() => undefined)
  // ) : void
  // {
  //   this.open(componentClass, configuration).then(
  //     (dialog : DialogRef<any>) => {
  //       dialog.result.then(followup, failure);
  //   });
  // }

  public observe<T> (
    componentClass : ContainerContent,
    configuration : {[key : string] : any} = {},
  ) : Observable<T | null>
  {
    return Observable.create((o : Observer<T>) => {
      this.open(componentClass, configuration).then(
        (dialog : DialogRef<any>) => {
        dialog.result.then(
          (result : T) => {
            o.next(result);
            o.complete();
          },
          () => {
            if ("dismissValue" in configuration)
            {
              o.next(configuration.dismissValue);
            }
            o.complete();
          }
        );
      });
    });
  }

  private buildConfirmation (context : ConfirmationContext)
    : Promise<DialogRef<TwoButtonPreset>>
  {
    const d : TwoButtonPresetBuilder = this.modalProvider.confirm()
      .title(context.title)
      .body(context.body);
    if (context.yesLabel)
    {
      d.okBtn(context.yesLabel);
    }
    if (context.cancelLabel)
    {
      d.cancelBtn(context.cancelLabel);
    }
    if (context.noLabel)
    {
      d.addButton("btn btn-warning", context.noLabel,
        (cmp : ModalComponent<any>) => cmp.dialog.close(false));
    }
    return d.open();
  }

  public confirm (context : ConfirmationContext)
    : Observable<boolean>
  {
    return Observable.create((o : Observer<boolean>) => {
      const modalPromise : Promise<DialogRef<any>>
        = this.buildConfirmation(context);
      modalPromise.then((dialog : DialogRef<any>) => {
        dialog.result.then(
          (result : boolean) => {
            o.next(result);
            o.complete();
          },
          () => {
            if ("dismissValue" in context)
            {
              o.next(context.dismissValue);
            }
            o.complete();
          }
        );
      });
    });
  }

  public alert (msg : string | MessageContext) : void
  {
    const context : MessageContext = (typeof msg === "string")
      ? {title: "Warning", body: msg}
      : msg;
    this.modalProvider.alert().body(context.body).title(context.title).open();
  }

  // public inspect (item : Inspectable) : void
  // {
  //   // provide a method to spin up a modal with some structure but that
  //   // doesn't require a full component class... too dynamic?
  // }

  public stringPrompt (context : PromptContext<string>) : Observable<string>
  {
    return Observable.create((o : Observer<string>) => {
      const modalPromise : Promise<DialogRef<any>>
        = this.modalProvider.prompt()
          .title(context.title)
          .body(context.body)
          .placeholder(context.placeholder)
          .defaultValue(context.initialValue || "")
          .open();
      modalPromise.then((dialog : DialogRef<any>) => {
        dialog.result.then(
          (result : string | null) => {
            if (context.acceptEmptyResult === true ||
              (typeof result === "string" && result.length > 0))
            {
              o.next(result);
            }
            o.complete();
          },
          () => {
            o.complete();
          }
        );
      });
    });
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/modal.service.ts